import { CMMBranch, OnboardingChatModule, WidgetType } from '@fruitful/types';

import type { MastraThreadMessage } from '../types/MastraThreadMessage';
import { CONVERSATIONAL_MM_WELCOME_RESPONSE_KEY } from './conversationalMMWelcome';
import { parseMultiSelectConfirmedMessage } from './multiSelectMessages';
import { isRecord } from './parsingHelpers';
import type { ThreadMetricsSurfaceState } from './threadMetricsPatch';

const WELCOME_OPTION_TO_BRANCH: Record<string, CMMBranch> = {
    'get-out-of-debt': CMMBranch.GoalsDebt,
    'save-for-something': CMMBranch.GoalsSavings,
    'create-money-system': CMMBranch.MoneySystem,
    'create-money-system-v2': CMMBranch.GoalsSystem,
    'start-investing': CMMBranch.StartInvesting,
};

interface MessagePart {
    type?: string;
    text?: string;
    toolName?: string;
    toolInvocation?: { toolName?: string; name?: string };
    data?: {
        widget?: string;
        props?: Record<string, unknown> | null;
    };
}

const isMessagePart = (value: unknown): value is MessagePart =>
    isRecord(value) && typeof value.type === 'string';

const extractParts = (message: MastraThreadMessage): MessagePart[] => {
    if (Array.isArray(message.parts)) {
        return message.parts.filter(isMessagePart);
    }

    if (isRecord(message.content) && Array.isArray(message.content.parts)) {
        return (message.content.parts as unknown[]).filter(isMessagePart);
    }

    if (Array.isArray(message.content)) {
        return (message.content as unknown[]).filter(isMessagePart);
    }

    return [];
};

const extractTextContent = (message: MastraThreadMessage): string => {
    if (typeof message.content === 'string') {
        return message.content;
    }

    if (
        isRecord(message.content) &&
        typeof message.content.content === 'string'
    ) {
        return message.content.content;
    }

    const parts = extractParts(message);
    return parts
        .filter((p) => p.type === 'text' && typeof p.text === 'string')
        .map((p) => p.text as string)
        .join('');
};

const inferBranchFromWelcomeMessage = (
    messages: MastraThreadMessage[],
): CMMBranch | null => {
    for (const message of messages) {
        if (message.role !== 'user') {
            continue;
        }

        const text = extractTextContent(message);
        const payload = parseMultiSelectConfirmedMessage(text);
        if (payload == null) {
            continue;
        }

        if (payload.responseKey !== CONVERSATIONAL_MM_WELCOME_RESPONSE_KEY) {
            continue;
        }

        for (const optionId of payload.selectedOptionIds) {
            const branch = WELCOME_OPTION_TO_BRANCH[optionId];
            if (branch != null) {
                return branch;
            }
        }
    }

    return null;
};

/**
 * Infers ThreadMetricsSurfaceState from restored thread messages by scanning
 * for known UI widgets and the welcome branch selection.
 * Used to restore the progress bar on page reload.
 */
export const inferThreadMetadataFromMessages = (
    messages: MastraThreadMessage[],
): ThreadMetricsSurfaceState | null => {
    let branch: CMMBranch | null = null;
    let hasAhaMoment = false;
    let hasSpendGoals = false;
    let hasBillsTable = false;
    let hasSavingsGoalCard = false;
    let hasDebtGoalCard = false;
    let hasDebtCheckAnswer = false;
    const submitToolsSeen = new Set<string>();

    for (const message of messages) {
        if (message.role === 'user') {
            const text = extractTextContent(message);
            const payload = parseMultiSelectConfirmedMessage(text);
            if (
                payload?.responseKey === 'goalsDebt.hasHighInterestDebt' &&
                payload.selectedOptionIds.includes('no')
            ) {
                hasDebtCheckAnswer = true;
            }
            if (
                payload?.responseKey === 'goalsDebt.debtTypes' &&
                payload.selectedOptionIds.includes('no-high-interest-debt')
            ) {
                hasDebtCheckAnswer = true;
            }
            continue;
        }

        if (message.role !== 'assistant') {
            continue;
        }

        if (
            isRecord(message.content) &&
            Array.isArray(message.content.toolInvocations)
        ) {
            for (const inv of message.content.toolInvocations as unknown[]) {
                if (!isRecord(inv)) continue;
                const toolName =
                    typeof inv.toolName === 'string'
                        ? inv.toolName
                        : typeof inv.name === 'string'
                          ? inv.name
                          : null;
                if (toolName?.startsWith('submit-') === true) {
                    submitToolsSeen.add(toolName.replace('submit-', ''));
                }
            }
        }

        for (const part of extractParts(message)) {
            if (part.type === 'tool-invocation') {
                const toolName =
                    part.toolInvocation?.toolName ?? part.toolInvocation?.name;
                if (
                    typeof toolName === 'string' &&
                    toolName.startsWith('submit-')
                ) {
                    submitToolsSeen.add(toolName.replace('submit-', ''));
                }
            }

            if (part.type === 'tool-call') {
                const toolName = part.toolName;
                if (
                    typeof toolName === 'string' &&
                    toolName.startsWith('submit-')
                ) {
                    submitToolsSeen.add(toolName.replace('submit-', ''));
                }
            }

            if (part.type !== 'data-ui' || part.data?.widget == null) {
                continue;
            }

            if (
                part.data.widget === WidgetType.AhaMoment &&
                isRecord(part.data.props)
            ) {
                hasAhaMoment = true;
                const intake = part.data.props.intake;
                if (isRecord(intake) && typeof intake.branch === 'string') {
                    branch = intake.branch as CMMBranch;
                }
            }

            if (part.data.widget === WidgetType.SpendGoals) {
                hasSpendGoals = true;
            }

            if (
                part.data.widget === WidgetType.InlineDataTable &&
                isRecord(part.data.props) &&
                part.data.props.accountColumnHeader === 'Bills (estimated)'
            ) {
                hasBillsTable = true;
            }

            if (
                part.data.widget === WidgetType.GoalSummaryCard &&
                isRecord(part.data.props)
            ) {
                const image = part.data.props.image;
                if (image === 'secure-savings.png') {
                    hasSavingsGoalCard = true;
                }
                if (image === 'discover.png' || image == null) {
                    hasDebtGoalCard = true;
                }
            }
        }
    }

    if (branch == null) {
        branch = inferBranchFromWelcomeMessage(messages);
    }

    if (branch == null) {
        return null;
    }

    const collectedModules: string[] = [];

    if (hasAhaMoment) {
        collectedModules.push(
            OnboardingChatModule.Savings,
            OnboardingChatModule.DebtCheck,
            OnboardingChatModule.Debt,
            OnboardingChatModule.Income,
            OnboardingChatModule.CoreNumbers,
        );
    } else {
        // Use all available signals: tool invocations + widgets + user answers
        for (const mod of submitToolsSeen) {
            collectedModules.push(mod);
        }
        if (
            hasSavingsGoalCard &&
            !collectedModules.includes(OnboardingChatModule.Savings)
        ) {
            collectedModules.push(OnboardingChatModule.Savings);
        }
        if (
            hasDebtGoalCard &&
            !collectedModules.includes(OnboardingChatModule.Debt)
        ) {
            collectedModules.push(OnboardingChatModule.Debt);
        }
        if (
            hasDebtCheckAnswer &&
            !collectedModules.includes(OnboardingChatModule.DebtCheck)
        ) {
            collectedModules.push(OnboardingChatModule.DebtCheck);
        }
        if (hasSpendGoals || hasBillsTable) {
            if (!collectedModules.includes(OnboardingChatModule.DebtCheck)) {
                collectedModules.push(OnboardingChatModule.DebtCheck);
            }
            if (!collectedModules.includes(OnboardingChatModule.Income)) {
                collectedModules.push(OnboardingChatModule.Income);
            }
        }
        if (
            hasSpendGoals &&
            !collectedModules.includes(OnboardingChatModule.CoreNumbers)
        ) {
            collectedModules.push(OnboardingChatModule.CoreNumbers);
        }
    }

    return {
        branch,
        currentStep: hasAhaMoment ? 'results' : null,
        collectedModules,
    };
};
